import { useTranslation } from "providers/i18n"
import {
  Typography,
  CardMedia,
  CardContent,
  Stack,
  Box,
  useTheme,
  Card,
  SxProps,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { LocationIcon } from "components/icons"
import { getDurationDate } from "utils/date"
import {
  getFeatureDetail,
  IEventDetail,
  StatusByDateRange,
} from "utils/feature"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { useMemo } from "react"
import { getIcon } from "utils/icon"
import { IFeature } from "interfaces"
import { HighlightChip } from "components/chips/HighlightChip"
import { ComingSoonChip } from "components/chips"
import clsx from "clsx"

interface IVerticalEventCardProps {
  event: IFeature
  dense?: boolean
  sx?: SxProps
  className?: string
}

export const VerticalEventCard = ({
  event,
  dense = false,
  sx = {},
  className,
}: IVerticalEventCardProps) => {
  const { t } = useTranslation()
  const {
    name,
    coverImage,
    venueLogo,
    startDate,
    endDate,
    hasLocation,
    isAd,
    status,
  } = getFeatureDetail(event) as IEventDetail

  const durationText = useMemo(
    () => getDurationDate(startDate, endDate),
    [startDate, endDate]
  )

  const theme = useTheme()

  const { styleOverrides } = theme.components?.[`VerticalEventCard`] || {}
  const {
    root,
    cardMedia,
    cardContent,
    cardContentStack,
    venueLogoContainer,
    chipContainer,
    highlightChip,
    comingSoonChip,
    durationContainer,
    duration,
    cardTitle,
    cardInfoContainer,
    iconContainer,
    locationInfo,
  } = styleOverrides

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  return (
    <Card
      sx={deepmerge(root, sx)}
      className={clsx({ "MuiCardContent-dense": dense }, className)}
    >
      <CardMedia sx={cardMedia} image={coverImage} className={className} />

      <CardContent
        sx={cardContent}
        className={clsx("VerticalEventCard-cardContent", className)}
      >
        <Stack spacing={1} sx={cardContentStack}>
          {venueLogo && (
            <Box sx={venueLogoContainer} className={className}>
              <CardMedia component="img" image={venueLogo} />
            </Box>
          )}
          <Box sx={chipContainer}>
            {isAd && <HighlightChip sx={highlightChip} className={className} />}
            {status === StatusByDateRange.Upcoming && !isAd && (
              <ComingSoonChip
                sx={comingSoonChip}
                className={clsx("VerticalEventCard-comingSoonChip", className)}
              />
            )}
          </Box>
          <Box sx={durationContainer}>
            <Typography
              className={clsx("VerticalEventCard-durationText", className)}
              sx={duration}
              component="p"
              variant="subTitle"
            >
              {durationText}
            </Typography>
          </Box>
          <Typography
            sx={cardTitle}
            variant="subTitle"
            component="div"
            color="text.primary"
            className={clsx("VerticalEventCard-cardTitleText", className)}
          >
            {t(name)}
          </Typography>
        </Stack>
        {hasLocation && (
          <Box sx={cardInfoContainer}>
            <Box
              sx={iconContainer}
              color="text.secondary"
              className={className}
            >
              {locationIcon}
            </Box>
            <LocationInfo
              feature={event}
              variant="subTitle"
              color="text.secondary"
              sx={locationInfo}
              className={className}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
